/**
 * Removes all non-digit characters from a string and strips leading zeros.
 *
 *
 * @param inputValue - The input string to process.
 * @param keepLeadingZeros - If true, allows a single leading zero; otherwise all leading zeros are removed.
 * @returns A string containing only digits, with leading zeros handled according to `keepLeadingZeros`.
 *
 *
 * @example
 * stripToDigits('123-45-6789') // returns '1234567'
 * stripToDigits('(555) 123-4567') // returns '5551234567'
 * stripToDigits('00123') // returns '123' (if `keepLeadingZeros` is set to false)
 * stripToDigits('00123') // returns '00123' (if `keepLeadingZeros` is set to true)
 * stripToDigits('000') // returns '000'
 */
export function stripToDigits(inputValue: string,keepLeadingZeros?: boolean): string {
  const digits = inputValue.replace(/\D/g, '');
  return keepLeadingZeros ? digits : digits.replace(/^0+(?=\d)/, '');
}

/**
 * Formats a string of digits with thousand separators (using periods).
 * @param value - The numeric string or number to format, or undefined/null/empty string
 * @returns A formatted string with periods as thousand separators (e.g., "1000" or 1000 becomes "1.000")
 * @example
 * formatNumberWithThousandsSeparator("1000") // "1.000"
 * formatNumberWithThousandsSeparator(1000000) // "1.000.000"
 * formatNumberWithThousandsSeparator("1234567") // "1.234.567"
 * formatNumberWithThousandsSeparator("") // ""
 * formatNumberWithThousandsSeparator(undefined) // ""
 */
export function formatNumberWithThousandsSeparator(
  value?: string | number,
): string {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value !== 'string' && typeof value !== 'number') return '';

  const valueAsString = String(value);
  return valueAsString.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
